import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { AlertCircle, ChevronRight, X, FileText, ShieldCheck, RotateCcw, Info, Download } from "lucide-react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import useApi from "../utils/useApi"
import apis from "../utils/apis"
import { useTranslation } from "react-i18next"
import { BACKUP_EMERGENCY_CONTACT, DEFAULT_CATEGORY_ID } from "../utils/constants"
import { useSelector } from "react-redux"
import { usePWAInstall } from "../hooks/usePWAInstall"

export default function Drawer({ isOpen, onClose, categories: propCategories }) {
  const { t } = useTranslation()
  const { categoryId } = useParams()
  const navigate = useNavigate()
  const { callApi: getCategories } = useApi()
  const user = useSelector((state) => state.user.userInfo);
  const emergencyContact = user?.defaultAddress?.nearby?.mart?.mobile || BACKUP_EMERGENCY_CONTACT;
  const { isInstalled, promptInstall } = usePWAInstall();

  // Use React Query to fetch categories (will use cached data from HomePage)
  const { data: fetchedCategories, isLoading } = useQuery({
    queryKey: ['categories', DEFAULT_CATEGORY_ID],
    queryFn: async () => {
      return await getCategories({
        api: apis.listCategory,
        query: { parentCategory: DEFAULT_CATEGORY_ID },
      });
    },
    enabled: !propCategories && isOpen,
  });

  // Use either prop categories or fetched categories
  const categories = propCategories || fetchedCategories || [];

  const handleCategorySelect = (categoryId) => {
    navigate(`/products/v2/${categoryId}`);
    onClose()
  }

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      {/* Drawer */}
      <motion.div
        initial={{ x: "-100%" }}
        animate={{ x: isOpen ? 0 : "-100%" }}
        transition={{
          type: "spring",
          damping: 30,
          stiffness: 300,
          mass: 0.8,
        }}
        className="fixed top-0 left-0 h-full w-4/5 max-w-xs bg-white section shadow-2xl z-50 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t("drawer.title")}</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        {/* Categories - scrollable */}
        <div className="flex-1 overflow-y-auto py-2 px-1">
          {isLoading ? (
            <div className="space-y-2 p-2">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="animate-pulse flex items-center p-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-md w-3/4"></div>
                  <div className="ml-auto h-4 w-4 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                </div>
              ))}
            </div>
          ) : (
            <ul className="space-y-1 p-2">
              {categories?.map((category) => (
                <motion.li key={category._id} whileTap={{ scale: 0.98 }}>
                  <button
                    onClick={() => handleCategorySelect(category._id)}
                    className={`
                      w-full text-left py-3 px-4 rounded-lg transition-all duration-200
                      flex items-center justify-between
                      font-medium capitalize hover:!bg-gray-100 dark:hover:!bg-border-200
                      ${
                        categoryId === category?._id
                          && "selected"
                      }
                    `}
                  >
                    <span>{category.name}</span>
                    <ChevronRight
                      className={`
                      w-4 h-4 transition-transform duration-200
                      ${categoryId === category?._id ? "opacity-100" : "opacity-50"}
                    `}
                    />
                  </button>
                </motion.li>
              ))}
            </ul>
          )}
        </div>

        {/* Legal Links - always visible, never scrolled away */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 px-3 pt-2 pb-1">
          <p className="text-xs text-gray-400 dark:text-gray-500 pb-1 uppercase tracking-wide font-medium">{t("legal.sectionTitle")}</p>
          {[
            { to: "/terms", labelKey: "legal.terms", icon: FileText },
            { to: "/privacy", labelKey: "legal.privacy", icon: ShieldCheck },
            { to: "/refund-policy", labelKey: "legal.refund", icon: RotateCcw },
            { to: "/about", labelKey: "legal.about", icon: Info },
          ].map(({ to, labelKey, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              onClick={onClose}
              className="flex items-center justify-between w-full py-2 px-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t(labelKey)}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 opacity-50" />
            </Link>
          ))}

          {/* Install App — hidden once already installed as a PWA */}
          {!isInstalled && (
            <button
              onClick={() => { promptInstall(); onClose(); }}
              className="flex items-center justify-between w-full py-2 px-1 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-800 transition-colors mt-1"
            >
              <div className="flex items-center gap-2.5">
                <Download className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                <div className="text-left">
                  <span className="text-sm font-medium text-primary-600 dark:text-primary-400 block">{t("drawer.installApp")}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">{t("drawer.installAppSubtitle")}</span>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-primary-400 opacity-70" />
            </button>
          )}
        </div>

        {/* Emergency Message - Outside scroll area so it never overlaps content */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-500 text-white p-4 shadow-lg rounded-t-lg flex-shrink-0">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-bold mb-1">
                {t("drawer.emergencyMessage")}{" "}
                <a
                  href={`tel:${emergencyContact}`}
                  className="underline hover:no-underline"
                >
                  {emergencyContact}
                </a>
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  )
}

